import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Building2, Briefcase, CheckCircle2, Upload } from "lucide-react";
import PublicAssistant from "../components/PublicAssistant";
import { BrandLockup } from "../components/Brand";

function formatDetail(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong. Please try again.";
}

export default function CareersJob() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience_years: "", expected_ctc: "", notice_period: "", location: "", resume_text: "" });
  const [parsing, setParsing] = useState(false);

  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/public/parse-resume`, fd);
      setForm((f) => ({ ...f, resume_text: data.text }));
      toast.success(`Resume parsed — ${data.chars.toLocaleString()} characters extracted`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/jobs/${id}`)
      .then((r) => setJob(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/public/apply`, {
        job_id: id, ...form, experience_years: +form.experience_years || 0,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
      <div className="text-sm text-muted-foreground">This role is no longer open.</div>
      <Link to="/careers" className="text-sm text-[#0A251D] hover:underline">← Back to all roles</Link>
    </div>
  );
  if (!job) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="careers-job-page">
      <header className="bg-[#0A251D] text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/careers" className="hover:opacity-80 transition-opacity duration-150"><BrandLockup light compact /></Link>
          <Link to="/login" className="text-xs uppercase tracking-widest font-bold border border-white/40 px-5 py-2.5 rounded-full hover:bg-white hover:text-[#0A251D] transition-colors duration-150">Employee Sign in</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <Link to="/careers" data-testid="back-to-careers" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-[#0A251D]"><ArrowLeft size={13} /> All roles</Link>

        {submitted ? (
          <div className="border bg-white rounded-xl p-10 mt-4 text-center" data-testid="application-success">
            <CheckCircle2 size={40} className="text-emerald-600 mx-auto" />
            <h1 className="font-heading font-black text-2xl tracking-tight mt-4">Application received!</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
              Thanks for applying to <strong>{job.title}</strong> at {job.entity_name}. TalentOS AI is already
              screening your profile against the role — our recruitment team will review it and reach out
              within 3 business days. You can ask the AI assistant anything meanwhile.
            </p>
            <Link to="/careers" className="inline-block mt-6 bg-[#0A251D] text-white px-6 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Browse more roles</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-5 mt-4">
            <div className="lg:col-span-3">
              <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{job.title}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Building2 size={13} /> {job.entity_name}</span>
                <span className="inline-flex items-center gap-1"><MapPin size={13} /> {job.location_name}</span>
                <span className="inline-flex items-center gap-1"><Briefcase size={13} /> {job.experience_min}–{job.experience_max} yrs</span>
              </div>
              {job.skills && <div className="mt-3 text-sm"><span className="text-[#0A251D] font-semibold">Skills:</span> {job.skills}</div>}
              <div className="border bg-white rounded-xl p-6 mt-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Job Description</div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="job-description">{job.job_description || "Details shared during the process."}</div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <form onSubmit={apply} className="border bg-white rounded-xl p-6 sticky top-6" data-testid="apply-form">
                <div className="font-heading font-bold text-lg tracking-tight">Apply now</div>
                <div className="text-xs text-muted-foreground mt-0.5 mb-4">Takes under 2 minutes.</div>
                {[["name", "Full name *", "text", true], ["email", "Email *", "email", true], ["phone", "Phone / WhatsApp", "text", false]].map(([k, l, t, req]) => (
                  <div key={k} className="mb-3">
                    <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">{l}</label>
                    <input data-testid={`apply-${k}-input`} type={t} required={req} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="mt-1 w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A251D]" />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Experience (yrs)</label>
                    <input data-testid="apply-experience-input" type="number" min="0" value={form.experience_years}
                      onChange={(e) => setForm({ ...form, experience_years: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Expected CTC</label>
                    <input value={form.expected_ctc} onChange={(e) => setForm({ ...form, expected_ctc: e.target.value })}
                      placeholder="e.g. 18 LPA" className="mt-1 w-full border px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Notice period</label>
                    <input value={form.notice_period} onChange={(e) => setForm({ ...form, notice_period: e.target.value })}
                      placeholder="e.g. 30 days" className="mt-1 w-full border px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Current city</label>
                    <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Resume *</label>
                  <label data-testid="apply-resume-upload" className={`mt-1 flex items-center justify-center gap-2 border-2 border-dashed rounded-lg px-3 py-4 text-sm cursor-pointer transition-colors duration-150 ${parsing ? "opacity-60" : "hover:border-[#E05A47] hover:text-[#E05A47]"}`}>
                    <Upload size={15} />
                    {parsing ? "Extracting text…" : "Upload PDF / DOCX / TXT"}
                    <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={uploadResume} disabled={parsing} />
                  </label>
                  <textarea data-testid="apply-resume-textarea" required rows={5} value={form.resume_text}
                    onChange={(e) => setForm({ ...form, resume_text: e.target.value })}
                    placeholder="…or paste your resume text / experience summary here"
                    className="mt-2 w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05A47]" />
                  <div className="text-[11px] text-muted-foreground mt-1">Your resume is instantly screened by TalentOS AI against this role.</div>
                </div>
                <button data-testid="apply-submit-button" disabled={busy}
                  className="w-full bg-[#0A251D] text-white py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-60">
                  {busy ? "Submitting…" : "Submit Application"}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
      <PublicAssistant />
    </div>
  );
}
